import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  file: File;
  onCancel: () => void;
  onReady: (file: File) => void;
}

const MAX_EDGE = 1600;

/** A deliberately small cropper: one movable rectangle, no extra native dependency. */
export function ImageCropDialog({ file, onCancel, onReady }: Props) {
  const { t } = useTranslation();
  const image = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const preview = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const [loaded, setLoaded] = useState(false);
  const [url, setUrl] = useState('');

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  function save() {
    const source = image.current;
    if (!source) return;
    const cropWidth = source.naturalWidth * crop.w / scale;
    const cropHeight = source.naturalHeight * crop.h / scale;
    const shrink = Math.min(1, MAX_EDGE / Math.max(cropWidth, cropHeight));
    const width = Math.round(cropWidth * shrink);
    const height = Math.round(cropHeight * shrink);
    const canvas = document.createElement('canvas');
    const quarterTurn = rotation % 180 !== 0;
    canvas.width = quarterTurn ? height : width;
    canvas.height = quarterTurn ? width : height;
    const context = canvas.getContext('2d');
    if (!context) return;
    const x = source.naturalWidth * crop.x;
    const y = source.naturalHeight * crop.y;
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((rotation * Math.PI) / 180);
    context.drawImage(source, x, y, cropWidth, cropHeight, -width / 2, -height / 2, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      onReady(new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.85);
  }

  function point(event: React.PointerEvent) {
    const rect = preview.current!.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  }

  return <div className="cropper" role="dialog" aria-modal="true" aria-label={t('images.cropTitle')}>
    <div className="cropper__backdrop" onClick={onCancel} />
    <div className="cropper__body">
      <h3>{t('images.cropTitle')}</h3>
      <p className="hint">{t('images.cropHint')}</p>
      <div ref={preview} className="cropper__preview" onPointerDown={(event) => { if (!loaded) return; event.currentTarget.setPointerCapture(event.pointerId); setStart(point(event)); }} onPointerMove={(event) => { if (!start) return; const end = point(event); setCrop({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), w: Math.abs(end.x - start.x), h: Math.abs(end.y - start.y) }); }} onPointerUp={() => setStart(null)}>
        <img ref={image} src={url} alt="" style={{ transform: `rotate(${rotation}deg) scale(${scale})` }} onLoad={() => setLoaded(true)} />
        <div className="cropper__selection" style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }} />
      </div>
      <label>{t('images.zoom')}<input type="range" min="1" max="1.5" step="0.01" value={scale} onChange={(event) => setScale(Number(event.target.value))} disabled={!loaded} /></label>
      <div className="panel__actions"><button type="button" onClick={() => setRotation((value) => (value + 90) % 360)} disabled={!loaded}>{t('images.rotate')}</button><button type="button" onClick={onCancel}>{t('common.cancel')}</button><button type="button" className="primary" onClick={save} disabled={!loaded}>{t('common.save')}</button></div>
    </div>
  </div>;
}
